import {Inject, Injectable} from '@angular/core';
import firebase from 'firebase/compat/app';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {FirebaseApp} from '@angular/fire/compat';
import {RSDialog} from '../../libs/rsnotify/rsdialog';
import {RSDebuggerService} from '../../libs/rsdebugger/rsdebugger.service';
import {AngularFireFunctions} from '@angular/fire/compat/functions';
import {RSLoadingModalService} from '../../libs/rsloading/rsloading.service';
import {environment} from '../../environments/environment';
import {RSDialogService} from '../../libs/rsnotify/rsdialog.service';
import {AngularFireAnalytics, AngularFireAnalyticsModule} from '@angular/fire/compat/analytics';
import { LocalStorage, CookiesStorageService, LocalStorageService, SessionStorageService, SharedStorageService } from '@efaps/ngx-store';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import {Hort360AccountUser} from "../../../functions/src/types";

@Injectable({
  providedIn: 'root'
})
export class UserService {
  analytics: AngularFireAnalytics = null;
  activeUserClaims: Hort360AccountUser = null;
  activeUserState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  get hasActiveUser() {
    return !!(this.activeUserState.getValue());
  }
  get isSuperUser() {
    return this.hasActiveUser && this.activeUserClaims?.grower > 2;
  }
  get activeUserDisplayName() {
    if (this.hasActiveUser) {
      return this.activeUserClaims?.firstname ? this.activeUserClaims?.firstname : 'User';
    }
    return 'User';
  }
  get activeUserId() {
    if (this.hasActiveUser) {
      return this.activeUserClaims?.id ? this.activeUserClaims?.id : -1;
    }
    return -1;
  }


  debugStatus: BehaviorSubject<boolean>;
  @LocalStorage('debug') private debugVal: boolean = false;
  set debug(bValue: any) {
    const bDebug = (bValue && (bValue === true || bValue === 'true' || bValue === 1));
    if (bDebug === this.debugVal && bDebug === this.rsDebugger.debugging) {
      return; // ignore
    }
    if (bDebug) {
      console.warn('UserService::: Debug Enabled.');
    } else {
      console.warn('UserService::: Debug Disabled.');
    }

    // Tell our debugger service what we're up to
    this.debugVal = bDebug;
    this.debugStatus.next(bDebug);
    this.rsDebugger.debugging = bDebug;
  }
  get debug() {
    return (this.debugVal && (this.debugVal === true || this.debugVal === 'true'));
  }

  constructor(
      private fbApp: FirebaseApp,
      private angularFireAuth: AngularFireAuth,
      private angularFireFunctions: AngularFireFunctions,
      private fbDb: AngularFirestore,
      private notifierService: RSDialogService,
      private rsDebugger: RSDebuggerService,
      private rsLoading: RSLoadingModalService,
      private angularFireAnalytics: AngularFireAnalytics,
      private localStorageService: LocalStorageService,
      private router: Router
  ) {
    console.log('UserService::: constructor');

    // Are we in Debug?
    this.debugStatus = new BehaviorSubject(this.rsDebugger.debugging);
    this.debug = this.rsDebugger.debugging;

    // Prepare the loading;
    this.fbLoading = new Promise((resolve => {
      this.fbLoadingResolve = resolve;
      setTimeout(() => {
        if (this.fbLoading && this.fbLoadingResolve) {
          console.warn('UserService::: unable to load Firebase in acceptable time.');
          this.fbAuthStateChange(null);
        }
      }, 5000); // Give up after 5 seconds?
    }));

    // Watch for sign in / out
    this.angularFireAuth.authState.subscribe((...args) => this.fbAuthStateChange(...args), (...args) => this.fbAuthStateChange(...args));
    this.angularFireAuth.user.subscribe((...args) => this.fbAuthUserChange(...args), (...args) => this.fbAuthUserChange(...args));
    this.angularFireAuth.idToken.subscribe((...args) => this.fbAuthIdToken(...args), (...args) => this.fbAuthIdToken(...args));
    this.angularFireAuth.idTokenResult.subscribe((...args) => this.fbAuthIdTokenResult(...args), (...args) => this.fbAuthIdTokenResult(...args));

    // Start Analytics
    this.analytics = this.angularFireAnalytics;

    // Watch and clear old values;
    this.activeUserState.subscribe(state => {
      if (!state) {
        this.activeUserClaims = null;
        this.analytics.setUserId(null);
      }
    });
  }

  // FB Handler to watch the status of the first load
  public fbLoading: Promise<void>;
  private fbLoadingResolve;
  private fbAuthStateChange(user: firebase.User) {
    console.log('fbAuthStateChange::: ', user);

    if (user) {
      console.log('fbAuthStateChange::: Logged in');
    } else {
      console.log('bAuthStateChange::: Logged out');
      if (this.hasActiveUser) {
        this.activeUserState.next(false);
        setTimeout(() => this.resolveAuthAwaiter(), 1000);
      }
    }
  }
  private fbAuthUserChange(user: firebase.User) {
    console.log('fbAuthUserChange::: ', user);

    // What'd we get?
    if (!user) {
      // Ignore
      this.activeUserState.next(false);
      setTimeout(() => this.resolveAuthAwaiter(), 1000);
      return;
    }

    // Set user id
    this.analytics.setUserId(user.uid);

    // Tell everyone
    this.activeUserState.next(true);
  }
  private fbAuthIdToken(token) {
    console.log('fbAuthIdToken::: ', token);
  }
  private fbAuthIdTokenResult(tokenResult) {
    console.log('fbAuthIdTokenResult::: ', tokenResult);
    this.activeUserClaims = tokenResult?.claims;
    if (this.activeUserClaims && !this.hasActiveUser) {
      this.activeUserState.next(true);
    }
    setTimeout(() => this.resolveAuthAwaiter());
  }

  private resolveAuthAwaiter() {
    if (this.fbLoading && this.fbLoadingResolve) {
      this.fbLoadingResolve();
      this.fbLoading = null;
      this.fbLoadingResolve = null;
    }
  }

  public async signOut() {
    // First, sign out;
    try {
      console.log('signInWithToken::: signOut');
      await this.angularFireAuth.signOut();
    } catch (e) {
      console.error(e);
    }

    // Wait for a nominal amount of time to allow things to catch up
    await new Promise(resolve1 => setTimeout(resolve1, 1000));
  }

  // Process the sign in with a server provided token
  public signInWithToken(token: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      if (this.hasActiveUser) {
        await this.signOut();
      }

      // Sign in with custom token!
      const userCredential = await this.angularFireAuth.signInWithCustomToken(token);

      // Get the user and their claims!
      const userToken = await userCredential.user.getIdTokenResult(true); // This will pull down the Idtoken and claims! :)
      console.log(userToken.claims);

      resolve();
    });
  }
}
